import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

const ActionsSection = ({ 
  selectedEightD,
  actions,
  showForm,
  setShowForm,
  editingAction,
  actionForm,
  onFormChange,
  onSave,
  onDelete,
  onEdit,
  onReset,
  onVerify, // Nueva función para verificar
  users,
  type, // 'containment', 'corrective', 'preventive'
  color, // 'yellow', 'green', 'blue'
  disciplineNumber // 'D3', 'D5', 'D7'
}) => {
  const { t } = useLanguage();

  if (!selectedEightD) return null;

  const getTitleKey = (type) => {
    const keys = {
      containment: 'actionsSection.containment',
      corrective: 'actionsSection.corrective',
      preventive: 'actionsSection.preventive'
    };
    return keys[type] || keys.corrective;
  };

  const getActionTypeKey = (type) => {
    const keys = {
      containment: 'actionsSection.containmentAction',
      corrective: 'actionsSection.correctiveAction',
      preventive: 'actionsSection.preventiveAction'
    };
    return keys[type] || keys.corrective;
  };

  const colorClasses = {
    yellow: {
      border: 'border-yellow-100',
      bg: 'bg-yellow-50',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      text: 'text-yellow-600',
      cardBorder: 'border-yellow-200',
      cardBg: 'bg-yellow-50'
    },
    green: {
      border: 'border-green-100',
      bg: 'bg-green-50',
      button: 'bg-green-600 hover:bg-green-700',
      text: 'text-green-600',
      cardBorder: 'border-green-200',
      cardBg: 'bg-green-50'
    },
    blue: {
      border: 'border-blue-100',
      bg: 'bg-blue-50',
      button: 'bg-blue-600 hover:bg-blue-700',
      text: 'text-blue-600',
      cardBorder: 'border-blue-200',
      cardBg: 'bg-blue-50'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  const getStatusColor = (status) => {
    const statusColors = {
      open: 'bg-yellow-100 text-yellow-800',
      under_analysis: 'bg-blue-100 text-blue-800',
      containment: 'bg-orange-100 text-orange-800',
      corrective: 'bg-purple-100 text-purple-800',
      monitoring: 'bg-indigo-100 text-indigo-800',
      closed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      done: 'bg-green-100 text-green-800',
      verified: 'bg-teal-100 text-teal-800',
      draft: 'bg-gray-100 text-gray-800',
      implemented: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg p-6 mb-6 border ${colors.border}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        {disciplineNumber} - {t(`acciones.${getTitleKey(type)}`)}
        <span className={`ml-3 text-sm font-normal ${colors.text}`}>
          ({selectedEightD.code})
        </span>
      </h2>

      {/* Botón para mostrar formulario */}
      {!showForm && !editingAction && (
        <div className="mb-4">
          <button
            onClick={() => setShowForm(true)}
            className={`px-4 py-2 ${colors.button} text-white font-bold rounded-lg`}
          >
            + {t('acciones.actionsSection.addAction')} {t(`acciones.${getActionTypeKey(type)}`)}
          </button>
        </div>
      )}

      {/* Formulario */}
      {(showForm || editingAction) && (
        <div className={`${colors.bg} p-4 rounded-lg mb-4`}>
          <h3 className="font-semibold text-gray-800 mb-3">
            {editingAction ? t('acciones.actionsSection.editAction') : t('acciones.actionsSection.newAction')} {t(`acciones.${getActionTypeKey(type)}`)}
          </h3>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('acciones.actionsSection.description')} *
            </label>
            <textarea
              name="description"
              value={actionForm.description}
              onChange={onFormChange}
              rows="3"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                {t('acciones.actionsSection.owner')}
              </label>
              <select
                name="owner_id"
                value={actionForm.owner_id}
                onChange={onFormChange}
                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
              >
                <option value="">{t('acciones.actionsSection.selectUser')}</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.username || user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                {t('acciones.actionsSection.dueDate')}
              </label>
              <input
                type="date"
                name="due_at"
                value={actionForm.due_at}
                onChange={onFormChange}
                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                {t('acciones.actionsSection.status')}
              </label>
              <select
                name="status"
                value={actionForm.status}
                onChange={onFormChange}
                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
              >
                <option value="draft">{t('acciones.actionsSection.statusDraft')}</option>
                <option value="in_progress">{t('acciones.actionsSection.statusInProgress')}</option>
                <option value="done">{t('acciones.actionsSection.statusDone')}</option>
                <option value="verified">{t('acciones.actionsSection.statusVerified')}</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onSave}
              className={`px-4 py-2 ${colors.button} text-white font-bold rounded-lg`}
            >
              {editingAction ? t('acciones.buttons.update') : t('acciones.buttons.add')}
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              {t('acciones.buttons.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Lista de acciones */}
      <div className="space-y-3">
        {actions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {t('acciones.actionsSection.noActions', { type: t(`acciones.${getTitleKey(type)}`).toLowerCase() })}
          </p>
        ) : (
          actions.map(action => (
            <div key={action.id} className={`border ${colors.cardBorder} rounded-lg p-4 ${colors.cardBg}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm text-gray-800 flex-1">{action.description}</p>
                    {action.verified_at && (
                      <span className="flex items-center gap-1 text-xs text-teal-700 bg-teal-100 px-2 py-1 rounded">
                        ✓ {t('acciones.actionsSection.verified')} {action.verified_at.substring(0, 10)}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-600">
                    {action.owner && <span>👤 {action.owner.username || action.owner.name}</span>}
                    {action.due_at && <span>📅 {action.due_at}</span>}
                    {action.verifier && (
                      <span>✓ {t('acciones.actionsSection.verifiedBy')} {action.verifier.username || action.verifier.name}</span>
                    )}
                  </div>
                  {action.evidence && action.evidence.length > 0 && (
                    <div className="mt-2 text-xs">
                      <span className="font-semibold text-gray-700">{t('acciones.actionsSection.evidence')}</span>
                      <ul className="list-disc list-inside ml-2 text-gray-600">
                        {action.evidence.map((ev, idx) => (
                          <li key={idx}>{ev.description || ev.type || t('acciones.actionsSection.evidenceItem')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-start ml-4 flex-col">
                  <div className="flex gap-2 items-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(action.status)}`}>
                      {t(`acciones.status.${action.status}`)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {onVerify && (
                      <button
                        onClick={() => onVerify(action)}
                        className={`px-2 py-1 ${action.verified_at ? 'bg-gray-50 text-gray-700 border-gray-300' : 'bg-teal-50 text-teal-700 border-teal-300'} border rounded text-xs font-semibold hover:opacity-80`}
                        title={action.verified_at ? t('acciones.actionsSection.unverify') : t('acciones.actionsSection.verify')}
                      >
                        {action.verified_at ? `✗ ${t('acciones.actionsSection.unverify')}` : `✓ ${t('acciones.actionsSection.verify')}`}
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(action)}
                      className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded text-xs font-semibold hover:bg-blue-100"
                    >
                      {t('acciones.buttons.edit')}
                    </button>
                    <button
                      onClick={() => onDelete(action.id)}
                      className="px-2 py-1 bg-red-50 text-red-700 border border-red-300 rounded text-xs font-semibold hover:bg-red-100"
                    >
                      {t('acciones.buttons.delete')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActionsSection;
